import React, { useMemo } from "react";
import { GetCounterpartiesQuery } from "@common/schemas/counterparty.schema";
import { FiltersTypes } from "@common/types/filters/general";
import { CounterpartyDirectoryView } from "@common/types/views/counterpartyDirectoryView";
import { View } from "@common/types/views/views";
import {
    FilterTabs,
    FiltersChangeFn,
} from "@components/pageHeader/filters/lib";
import { glDirectionRadios } from "@components/pageHeader/filters/Tabs/AmountFilter/directionRadios";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { DirectoryContext } from "@components/settings/Directory/Directory.context";
import { DirectoryFiltersForm } from "@components/settings/Directory/DirectoryFiltersForm";
import { DirectoryTable } from "@components/settings/Directory/DirectoryTable";
import { DirectoryFilters } from "@components/settings/Directory/lib";
import { useCounterpartyDirectoryViewConfig } from "@components/settings/Directory/useCounterpartyDirectoryViewConfig";
import { ColumnSettings } from "@design-system/ViewToolbar/ColumnSettings";
import { CollapseAllButton } from "@design-system/ViewTable/CollapseAllButton";
import { restrictViewConfigColumns } from "../shared/taskTableView";
import { LockedTaskFilters } from "../Transactions/LockedTaskFilters";
import { CounterpartiesTaskGlDrawer } from "./CounterpartiesTaskGlDrawer";
import { useCounterpartiesDirectoryContext } from "./useCounterpartiesDirectoryContext";
import { useCounterpartiesTaskRows } from "./useCounterpartiesTaskRows";
import {
    getCounterpartiesLockedActiveFilters,
    toCounterpartiesOverlayFilters,
} from "./counterpartiesTaskFilters";
import { omitTaskScope } from "./counterpartiesTaskView";

interface CounterpartiesTaskTableProps {
    queryScopeKey: string;
    view: CounterpartyDirectoryView;
    onViewChange: (view: View) => void;
    onFiltersChange: FiltersChangeFn;
    scope: DirectoryFilters;
    onScopeChange: (scope: DirectoryFilters) => void;
    availableFilters: FilterTabs[];
    defaultView: CounterpartyDirectoryView;
    allowedColumnIds?: ReadonlySet<string>;
    lockScope?: boolean;
    hideEntityFilter?: boolean;
    showBasisFilter?: boolean;
    collapseLockedFilters?: boolean;
    implicitFilterLabels?: string[];
    singleEntitySelection?: boolean;
    originalFilters?: FiltersTypes;
    stickyCounterpartiesIds?: string[];
    isEnabled?: boolean;
    headerActions?: React.ReactNode;
    extraQueryFilters?: Partial<GetCounterpartiesQuery>;
    reviewMode?: "review" | "unreview";
    emptyView: () => React.ReactNode;
    className?: string;
    tableClassName?: string;
    isFullPage?: boolean;
}

export const CounterpartiesTaskTable: React.FC<
    CounterpartiesTaskTableProps
> = ({
    queryScopeKey,
    view,
    onViewChange,
    onFiltersChange,
    scope,
    onScopeChange,
    availableFilters,
    defaultView,
    allowedColumnIds,
    lockScope = false,
    hideEntityFilter = false,
    showBasisFilter = false,
    collapseLockedFilters = false,
    implicitFilterLabels,
    singleEntitySelection,
    originalFilters,
    stickyCounterpartiesIds,
    isEnabled = true,
    headerActions,
    extraQueryFilters,
    reviewMode,
    emptyView,
    className,
    tableClassName,
    isFullPage = true,
}) => {
    const { viewConfig: directoryViewConfig } =
        useCounterpartyDirectoryViewConfig();
    const {
        contextValue,
        counterpartySort,
        expanded,
        setExpanded,
        resetRows,
        tableRef,
        glDrawerState,
        lastGlDrawerState,
        closeGlDrawer,
    } = useCounterpartiesDirectoryContext({
        view,
        onViewChange,
        scope,
        onScopeChange,
    });

    const { counterparties, hasExpandableRows } = useCounterpartiesTaskRows({
        queryScopeKey,
        scope,
        counterpartySort,
        viewFilters: view.filters,
        resetRows,
        originalFilters,
        stickyCounterpartiesIds,
        extraQueryFilters,
        isEnabled,
    });

    const columnSettingsConfig = useMemo(
        () =>
            restrictViewConfigColumns({
                config: directoryViewConfig,
                allowedColumnIds,
            }),
        [directoryViewConfig, allowedColumnIds],
    );

    const overlayFilters = useMemo(
        () => ({
            ...toCounterpartiesOverlayFilters(view.filters),
            entityIds: scope.entityIds,
        }),
        [view.filters, scope.entityIds],
    );
    const defaultOverlayFilters = useMemo(
        () => ({ entityIds: scope.entityIds }),
        [scope.entityIds],
    );
    const lockedActiveFilters = useMemo(
        () => getCounterpartiesLockedActiveFilters(originalFilters),
        [originalFilters],
    );

    return (
        <DirectoryContext.Provider value={contextValue}>
            <TablePageHeader
                search={scope.search}
                isFullPage={isFullPage}
                onSearchChange={(value) =>
                    onScopeChange({ ...scope, search: value })
                }
                className={className}
                filters={overlayFilters}
                defaultFilters={defaultOverlayFilters}
                onFiltersChange={onFiltersChange}
                directionRadios={glDirectionRadios}
                availableFilters={availableFilters}
                showAmountFilterLevel
                hideCoaAccountLastChangedBy
                afterFiltersChildren={
                    originalFilters || implicitFilterLabels?.length ? (
                        <LockedTaskFilters
                            lockedActiveFilters={lockedActiveFilters}
                            baseFilters={
                                originalFilters
                                    ? omitTaskScope(originalFilters)
                                    : {}
                            }
                            maxVisibleFilters={lockedActiveFilters.length}
                            implicitFilterLabels={implicitFilterLabels}
                            collapse={collapseLockedFilters}
                        />
                    ) : undefined
                }
            >
                <DirectoryFiltersForm
                    filters={scope}
                    setFilters={onScopeChange}
                    lockScope={lockScope}
                    hideEntityFilter={hideEntityFilter}
                    hideBasisFilter={!showBasisFilter}
                    singleEntitySelection={singleEntitySelection}
                />
                {hasExpandableRows && (
                    <CollapseAllButton
                        expanded={expanded}
                        tableRef={tableRef}
                    />
                )}
                <ColumnSettings
                    view={view}
                    viewConfig={columnSettingsConfig}
                    defaultView={defaultView}
                    onViewChange={onViewChange}
                />
                {headerActions}
            </TablePageHeader>
            <div className={tableClassName}>
                <DirectoryTable
                    counterpartiesData={counterparties.data}
                    isLoading={counterparties.isLoading || !isEnabled}
                    totalCounterparties={counterparties.data?.data.length ?? 0}
                    tableRef={tableRef}
                    expanded={expanded}
                    onExpandedChange={setExpanded}
                    reviewMode={reviewMode}
                    shouldExcludeNoCounterpartyRow
                    emptyView={emptyView}
                />
            </div>
            <CounterpartiesTaskGlDrawer
                isOpen={glDrawerState !== null}
                onClose={closeGlDrawer}
                drawerState={lastGlDrawerState}
                originalFilters={originalFilters}
            />
        </DirectoryContext.Provider>
    );
};
